<script setup>
// Astra DS · SharePopover — compartir lo que suena.
//
// Figma: `fase 2 · Pattern/SharePopover` 647:384.
//
// «Popover de compartir. Flota, por eso va en bg/elevated. showMsg enciende la
// confirmación.»
//
// ⚠️ El nodo lleva «fase 2» en el nombre: estaba planificado para más adelante.
// Se adelanta a petición de Pablo (26.08.26) porque el chip «Compartir» no
// hacía nada visible — copiaba al portapapeles en silencio, que es como no
// avisar de nada.
//
// El hueco de 1px entre celdas ES la línea: el contenedor va pintado del color
// del borde y las celdas llevan fondo opaco. Misma idea que TabBar y SiteFooter,
// y evita el píxel doble donde dos bordes se tocarían.
import { ref, computed, onMounted, onUnmounted } from "vue";
import ButtonDs from "@/components/ds/ButtonDs.vue";

const props = defineProps({
  url: { type: String, default: "" },
  // Lo que se dice al compartir: «Artista — Tema», normalmente.
  text: { type: String, default: "" },
});

const emit = defineEmits(["close"]);

const message = ref("");
const raiz = ref(null);
let limpiaMensaje = null;

const enlace = computed(() => props.url || window.location.href);
const titulo = computed(() => props.text || "Astra FM");

function avisar(texto) {
  message.value = texto;
  if (limpiaMensaje) clearTimeout(limpiaMensaje);
  limpiaMensaje = setTimeout(() => (message.value = ""), 2600);
}

function abrir(destino) {
  const u = encodeURIComponent(enlace.value);
  const t = encodeURIComponent(titulo.value);
  const rutas = {
    bluesky: `https://bsky.app/intent/compose?text=${t}%20${u}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
    x: `https://twitter.com/intent/tweet?text=${t}&url=${u}`,
  };
  window.open(rutas[destino], "_blank", "noopener,noreferrer,width=600,height=520");
}

async function copiar(aviso = "Enlace copiado") {
  try {
    await navigator.clipboard.writeText(`${titulo.value} · ${enlace.value}`);
    avisar(aviso);
  } catch (error) {
    // Sin permiso de portapapeles —o sin HTTPS— no se puede copiar. Decirlo es
    // mejor que un botón que no hace nada.
    avisar("No se pudo copiar");
  }
}

// Instagram no admite compartir un enlace desde la web: no hay intent, y para
// publicar haría falta generar una imagen. Hasta que exista esa pieza, el botón
// hace lo único honesto que puede hacer: dejar el enlace copiado para pegarlo.
// Está anotado como decisión abierta en REFACTOR.md.
function instagram() {
  copiar("Enlace copiado · pégalo en Instagram");
}

// Un popover que no se cierra con Escape ni al pinchar fuera es una trampa para
// el teclado: se abre y no hay forma de salir sin ratón.
function alPulsarTecla(e) {
  if (e.key === "Escape") emit("close");
}

function alPinchar(e) {
  if (raiz.value && !raiz.value.contains(e.target)) emit("close");
}

onMounted(() => {
  document.addEventListener("keydown", alPulsarTecla);
  // En `click` no: el mismo clic que abre el popover llegaría hasta aquí y lo
  // cerraría en el acto. `mousedown` con captura tampoco vale por lo mismo, así
  // que se escucha el clic del ciclo siguiente.
  setTimeout(() => document.addEventListener("click", alPinchar), 0);
});

onUnmounted(() => {
  document.removeEventListener("keydown", alPulsarTecla);
  document.removeEventListener("click", alPinchar);
  if (limpiaMensaje) clearTimeout(limpiaMensaje);
});
</script>

<template>
  <div ref="raiz" class="share-pop" role="dialog" aria-label="Compartir">
    <div class="share-pop__row">
      <ButtonDs
        class="share-pop__cell"
        variant="ghost"
        icono="bluesky"
        :icono-tam="16"
        etiqueta="Compartir en Bluesky"
        :lado="44"
        @click="abrir('bluesky')"
      />
      <ButtonDs
        class="share-pop__cell"
        variant="ghost"
        icono="facebook"
        :icono-tam="16"
        etiqueta="Compartir en Facebook"
        :lado="44"
        @click="abrir('facebook')"
      />
      <ButtonDs
        class="share-pop__cell"
        variant="ghost"
        icono="instagram"
        :icono-tam="16"
        etiqueta="Copiar el enlace para Instagram"
        :lado="44"
        @click="instagram"
      />
      <ButtonDs
        class="share-pop__cell"
        variant="ghost"
        icono="x"
        :icono-tam="16"
        etiqueta="Compartir en X"
        :lado="44"
        @click="abrir('x')"
      />
      <ButtonDs
        class="share-pop__cell"
        variant="ghost"
        icono="link"
        :icono-tam="16"
        etiqueta="Copiar el enlace"
        :lado="44"
        @click="copiar()"
      />
    </div>

    <!-- La confirmación ocupa sitio solo cuando hay algo que confirmar; si
         estuviera siempre, el popover cambiaría de alto al usarlo. -->
    <p v-if="message" class="share-pop__msg" role="status">{{ message }}</p>
  </div>
</template>

<style scoped lang="scss">
@use "@/assets/scss/type" as t;

// Flota, así que va en `bg/elevated` y cierra con borde fuerte: es lo único de
// la página que se levanta del plano, y tiene que leerse como una capa aparte.
.share-pop {
  position: absolute;
  z-index: 20;
  top: calc(100% + 6px);
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  box-sizing: border-box;
  border: 1px solid var(--color-border-strong);
  background: var(--color-bg-elevated);
}

// El hueco ES la línea: el contenedor pinta el color del borde y las celdas
// tapan todo menos ese píxel.
.share-pop__row {
  display: flex;
  gap: 1px;
  background: var(--color-border-default);
}

// Celdas de 44 sobre superficie elevada. **El hover rellena de rojo**, y aquí
// es lo correcto: son celdas pegadas, sin filete propio que marcar, y el rojo
// dice cuál se va a pulsar. Excepción escrita a la regla del 18.09.26.
.share-pop__cell {
  border: 0;
  background: var(--color-bg-elevated);
  color: var(--color-icon-primary);
  transition:
    background 0.12s linear,
    color 0.12s linear;

  &:hover,
  &:focus-visible {
    background: var(--color-bg-accent);
    color: var(--color-text-on-action);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: -2px;
  }
}

.share-pop__msg {
  @include t.mono-micro;
  margin: 0;
  padding: 8px 11px;
  border-top: 1px solid var(--color-border-default);
  color: var(--color-text-accent);
  white-space: nowrap;
}

@media (prefers-reduced-motion: reduce) {
  .share-pop__cell {
    transition: none;
  }
}
</style>
